import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { productAttributesApi, type AttributeSet, type AttributeTemplate, type Specification } from '../../api/productattributes';

export function AttributeSetsPage() {
  const [items, setItems] = useState<AttributeSet[]>([]);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');

  const reload = () => void productAttributesApi.listAttributeSets().then(setItems).catch((e) => setError(String(e)));
  useEffect(() => { reload(); }, []);

  const create = async () => {
    if (!name.trim() || !code.trim()) return;
    try {
      await productAttributesApi.createAttributeSet({ name: name.trim(), code: code.trim() });
      setName('');
      setCode('');
      reload();
    } catch (e) {
      setError(String(e));
    }
  };

  return (
    <div className="mx-auto max-w-6xl p-6">
      <Link to="/product-attributes" className="text-sm text-slate-600 hover:underline">← Attributes hub</Link>
      <h1 className="mt-4 text-2xl font-semibold">Attribute Sets</h1>
      <div className="mt-4 flex gap-2">
        <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Code" className="rounded-lg border px-3 py-2 text-sm" />
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" className="rounded-lg border px-3 py-2 text-sm" />
        <button type="button" onClick={() => void create()} className="rounded-lg bg-slate-900 px-4 py-2 text-sm text-white">Create set</button>
      </div>
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      <ul className="mt-6 space-y-2 rounded-xl border bg-white p-4 text-sm">
        {items.map((s) => (
          <li key={s.id} className="flex justify-between border-b py-2 last:border-0">
            <span className="font-medium">{s.name}</span>
            <span className="text-slate-500">{s.items?.length ?? 0} attributes</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function TemplateManagerPage() {
  const [items, setItems] = useState<AttributeTemplate[]>([]);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');

  const reload = () => void productAttributesApi.listTemplates().then(setItems).catch((e) => setError(String(e)));
  useEffect(() => { reload(); }, []);

  const create = async () => {
    if (!name.trim() || !code.trim()) return;
    try {
      await productAttributesApi.createTemplate({ name: name.trim(), code: code.trim() });
      setName('');
      setCode('');
      reload();
    } catch (e) {
      setError(String(e));
    }
  };

  return (
    <div className="mx-auto max-w-6xl p-6">
      <Link to="/product-attributes" className="text-sm text-slate-600 hover:underline">← Attributes hub</Link>
      <h1 className="mt-4 text-2xl font-semibold">Template Manager</h1>
      <div className="mt-4 flex gap-2">
        <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Code" className="rounded-lg border px-3 py-2 text-sm" />
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" className="rounded-lg border px-3 py-2 text-sm" />
        <button type="button" onClick={() => void create()} className="rounded-lg bg-slate-900 px-4 py-2 text-sm text-white">Create template</button>
      </div>
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      <ul className="mt-6 space-y-2 rounded-xl border bg-white p-4 text-sm">
        {items.map((t) => (
          <li key={t.id} className="flex justify-between border-b py-2 last:border-0">
            <span className="font-medium">{t.name}</span>
            <span className="text-slate-500">{t.attribute_ids?.length ?? 0} attributes</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function SpecificationBuilderPage() {
  const [items, setItems] = useState<Specification[]>([]);
  const [productId, setProductId] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');

  const reload = () => {
    void productAttributesApi.listSpecifications(productId ? { product_id: productId } : undefined).then(setItems).catch((e) => setError(String(e)));
  };
  useEffect(() => { reload(); }, [productId]);

  const create = async () => {
    if (!productId.trim() || !name.trim()) return;
    try {
      await productAttributesApi.createSpecification({
        product_id: productId.trim(),
        name: name.trim(),
        groups: [{ name: 'General', items: [{ label: 'Material', value: 'TBD' }] }],
      });
      setName('');
      reload();
    } catch (e) {
      setError(String(e));
    }
  };

  return (
    <div className="mx-auto max-w-6xl p-6">
      <Link to="/product-attributes" className="text-sm text-slate-600 hover:underline">← Attributes hub</Link>
      <h1 className="mt-4 text-2xl font-semibold">Specification Builder</h1>
      <div className="mt-4 flex flex-wrap gap-2">
        <input value={productId} onChange={(e) => setProductId(e.target.value)} placeholder="Product ID" className="rounded-lg border px-3 py-2 text-sm" />
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Specification name" className="rounded-lg border px-3 py-2 text-sm" />
        <button type="button" onClick={() => void create()} className="rounded-lg bg-slate-900 px-4 py-2 text-sm text-white">Create specification</button>
      </div>
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      <table className="mt-6 min-w-full rounded-xl border bg-white text-sm">
        <thead className="border-b bg-slate-50 text-left"><tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Product</th><th className="px-4 py-3">Groups</th></tr></thead>
        <tbody>
          {items.map((s) => (
            <tr key={s.id} className="border-b">
              <td className="px-4 py-3 font-medium">{s.name}</td>
              <td className="px-4 py-3">{s.product_id}</td>
              <td className="px-4 py-3">{s.groups?.length ?? 0}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
